/**
 * Distinguish Agent Studio graphs from AI Workflows graphs on import.
 * Both payloads expose `nodes` + `edges`, so type / node metadata must be inspected.
 */

export const AGENT_IMPORT_WORKFLOW_ERROR =
  'Import Failed: Selected file is a Workflow configuration, not an Agent'

const AGENT_NODE_TYPES = new Set([
  'start',
  'end',
  'agent',
  'router',
  'tool',
  'supervisor',
  'intent_classifier',
  'disambiguation',
  'hitl',
  'parallel',
  'rlm',
  'external_agent',
  'note',
])

/** Distinctive AI Workflows node types that never persist on an Agent canvas. */
const WORKFLOW_ONLY_NODE_TYPES = new Set([
  'fileReader',
  'ocr',
  'parser',
  'pmaEngine',
  'azureDocIntelligence',
  'chunker',
  'vectorDb',
  'azureAiSearch',
  'googleVectorSearch',
  'webScraper',
  'playwrightAutomation',
  'perplexity',
  'transformer',
  'ontologyBuilder',
  'ontologyGold',
  'ontologyKpi',
  'ontologyProcessor',
  'kgValidator',
  'outputConsole',
  'sqlReader',
  'nosqlReader',
  'graphReader',
  'sqlWriter',
  'huggingfaceBenchmark',
  'nosqlWriter',
  'graphWriter',
  'fileWriter',
  'fileGenerator',
  'imageGenerator',
  'aiModel',
  'syntheticDataGenerator',
  'documentSyntheticGenerator',
  'codeExecutor',
  'apiProcessor',
  'llmApiScraper',
  'messageReader',
  'messageWriter',
  'graphiti',
  'email',
])

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

function nodeTypeOf(node: Record<string, unknown>): string {
  if (typeof node.type === 'string' && node.type) {
    return node.type
  }
  const data = asRecord(node.data)
  if (!data) return ''
  if (typeof data.type === 'string' && data.type) return data.type
  if (typeof data.nodeType === 'string' && data.nodeType) return data.nodeType
  if (typeof data.framework === 'string' && data.framework) return data.framework
  return ''
}

function collectGraphNodes(data: Record<string, unknown>): Record<string, unknown>[] {
  const graph = asRecord(data.graph_definition)
  const nested = graph?.nodes
  if (Array.isArray(nested)) {
    return nested.filter((n): n is Record<string, unknown> => Boolean(asRecord(n)))
  }
  if (Array.isArray(data.nodes)) {
    return data.nodes.filter((n): n is Record<string, unknown> => Boolean(asRecord(n)))
  }
  return []
}

function metaEntityType(data: Record<string, unknown>): string {
  const meta = asRecord(data._meta)
  const entityType = meta?.entity_type
  return typeof entityType === 'string' ? entityType.toLowerCase() : ''
}

/**
 * True when YAML/JSON is an AI Workflows configuration rather than an Agent graph.
 */
export function isWorkflowConfiguration(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false
  const meta = metaEntityType(data)
  const nodeTypes = collectGraphNodes(data).map(nodeTypeOf)
  if (nodeTypes.some((t) => WORKFLOW_ONLY_NODE_TYPES.has(t))) {
    return true
  }
  if (meta === 'workflow' || meta === 'workflows') {
    return 'trigger_config' in data
  }
  return false
}

/**
 * True when every typed node is an Agent Studio type (or the graph has no typed nodes).
 */
export function isAgentGraphPayload(data: Record<string, unknown> | null | undefined): boolean {
  if (!data || isWorkflowConfiguration(data)) return false
  const nodeTypes = collectGraphNodes(data).map(nodeTypeOf).filter(Boolean)
  if (nodeTypes.length === 0) {
    return 'graph_definition' in data || Boolean(data.framework)
  }
  return nodeTypes.every((t) => AGENT_NODE_TYPES.has(t))
}
